import { Unzlib, zlibSync } from "fflate";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import { bytesFromBase64 } from "./binary";

const MAX_METADATA_BYTES = 32 * 1024 * 1024;
const fail = (): never => {
  throw new Error("BROWSER_STORAGE_INVALID");
};
function encode(bytes: Uint8Array): string {
  const chunks: string[] = [];
  for (let offset = 0; offset < bytes.length; offset += 8192)
    chunks.push(String.fromCharCode(...bytes.subarray(offset, offset + 8192)));
  return btoa(chunks.join(""));
}

/** Browser-only storage transform. Native projects retain complete PDF and ICC bytes. */
export function packBrowserState(state: unknown): string {
  const profiles: Record<string, string> = Object.create(null),
    pdfs: Record<string, string> = Object.create(null),
    references: string[][] = [],
    pdfReferences: string[][] = [];
  const visit = (value: unknown, path: string[]): unknown => {
    if (value instanceof Date) return value.toJSON();
    if (Array.isArray(value))
      return value.map((child, index) =>
        visit(child, [...path, String(index)]),
      );
    if (!value || typeof value !== "object") return value;
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => {
        const nextPath = [...path, key];
        if (
          key === "placedPdf" &&
          child &&
          typeof child === "object" &&
          !Array.isArray(child) &&
          Object.keys(child).sort().join(",") ===
            "assetId,data,height,sha256,version,width" &&
          child.version === 1 &&
          typeof child.assetId === "string" &&
          typeof child.width === "number" &&
          typeof child.height === "number" &&
          typeof child.data === "string" &&
          typeof child.sha256 === "string" &&
          /^[a-f0-9]{64}$/.test(child.sha256)
        ) {
          if (
            pdfs[child.sha256] !== undefined &&
            pdfs[child.sha256] !== child.data
          )
            return [key, visit(child, nextPath)];
          pdfs[child.sha256] = child.data;
          pdfReferences.push(nextPath);
          const { data: _data, ...descriptor } = child;
          return [key, { ...descriptor, dataRef: child.sha256 }];
        }
        if (
          key === "printProfile" &&
          child &&
          typeof child === "object" &&
          !Array.isArray(child) &&
          Object.keys(child).sort().join(",") === "data,name,sha256" &&
          typeof child.data === "string" &&
          typeof child.name === "string" &&
          typeof child.sha256 === "string" &&
          /^[a-f0-9]{64}$/.test(child.sha256)
        ) {
          if (
            profiles[child.sha256] !== undefined &&
            profiles[child.sha256] !== child.data
          )
            return [key, visit(child, nextPath)];
          profiles[child.sha256] = child.data;
          references.push(nextPath);
          return [
            key,
            { name: child.name, sha256: child.sha256, dataRef: child.sha256 },
          ];
        }
        return [key, visit(child, nextPath)];
      }),
    );
  };
  const packed = visit(state, []);
  if (pdfReferences.length) {
    const metadata = new TextEncoder().encode(
      JSON.stringify({ state: packed, references, pdfReferences }),
    );
    if (metadata.length > MAX_METADATA_BYTES)
      throw new Error("BROWSER_STORAGE_TOO_LARGE");
    return JSON.stringify({
      storageVersion: 3,
      metadata: {
        encoding: "zlib-base64",
        bytes: metadata.length,
        sha256: bytesToHex(sha256(metadata)),
        data: encode(zlibSync(metadata)),
      },
      printProfiles: profiles,
      placedPdfs: pdfs,
    });
  }
  // Retain the existing format until a workspace actually needs shared ICC bytes.
  return references.length
    ? JSON.stringify({
        storageVersion: 2,
        state: packed,
        printProfiles: profiles,
        references,
      })
    : JSON.stringify(packed);
}
export function unpackBrowserState(raw: string) {
  let parsed = JSON.parse(raw);
  let pdfReferences: unknown, pdfs: Record<string, unknown> | undefined;
  if (parsed?.storageVersion === 3) {
    const metadata = parsed.metadata;
    if (
      !metadata ||
      typeof metadata !== "object" ||
      Object.keys(metadata).sort().join(",") !== "bytes,data,encoding,sha256" ||
      metadata.encoding !== "zlib-base64" ||
      !Number.isSafeInteger(metadata.bytes) ||
      metadata.bytes < 1 ||
      metadata.bytes > MAX_METADATA_BYTES ||
      typeof metadata.sha256 !== "string" ||
      !/^[a-f0-9]{64}$/.test(metadata.sha256) ||
      typeof metadata.data !== "string" ||
      metadata.data.length < 4 ||
      metadata.data.length > 2 * MAX_METADATA_BYTES ||
      !/^[A-Za-z0-9+/]*={0,2}$/.test(metadata.data)
    )
      return fail();
    const compressed = bytesFromBase64(metadata.data),
      output = new Uint8Array(metadata.bytes);
    let offset = 0;
    const inflater = new Unzlib((chunk) => {
      if (offset + chunk.length > output.length) return fail();
      output.set(chunk, offset);
      offset += chunk.length;
    });
    // Small input chunks bound each intermediate expansion as well as the final buffer.
    for (let index = 0; index < compressed.length; index += 4096)
      inflater.push(
        compressed.subarray(index, index + 4096),
        index + 4096 >= compressed.length,
      );
    if (
      offset !== output.length ||
      bytesToHex(sha256(output)) !== metadata.sha256
    )
      return fail();
    const decoded = JSON.parse(
      new TextDecoder("utf-8", { fatal: true }).decode(output),
    );
    if (
      !decoded ||
      !Array.isArray(decoded.pdfReferences) ||
      !decoded.pdfReferences.length ||
      !parsed.placedPdfs ||
      typeof parsed.placedPdfs !== "object" ||
      Array.isArray(parsed.placedPdfs)
    )
      return fail();
    pdfReferences = decoded.pdfReferences;
    pdfs = parsed.placedPdfs;
    parsed = {
      storageVersion: 2,
      state: decoded.state,
      references: decoded.references,
      printProfiles: parsed.printProfiles,
    };
  }
  if (parsed?.storageVersion !== 2) return parsed;
  if (
    !parsed.state ||
    typeof parsed.state !== "object" ||
    !parsed.printProfiles ||
    typeof parsed.printProfiles !== "object" ||
    Array.isArray(parsed.printProfiles) ||
    !Array.isArray(parsed.references)
  )
    throw new Error("BROWSER_STORAGE_INVALID");
  for (const path of parsed.references) {
    if (
      !Array.isArray(path) ||
      !path.length ||
      path.at(-1) !== "printProfile" ||
      path.some((key: unknown) => typeof key !== "string")
    )
      throw new Error("PRINT_PROFILE_INVALID");
    let parent = parsed.state;
    for (const key of path.slice(0, -1)) {
      if (!parent || typeof parent !== "object" || !Object.hasOwn(parent, key))
        throw new Error("PRINT_PROFILE_INVALID");
      parent = parent[key];
    }
    if (
      !parent ||
      typeof parent !== "object" ||
      !Object.hasOwn(parent, "printProfile")
    )
      throw new Error("PRINT_PROFILE_INVALID");
    const value = parent.printProfile;
    if (
      !value ||
      typeof value !== "object" ||
      Object.keys(value).sort().join(",") !== "dataRef,name,sha256" ||
      value.dataRef !== value.sha256 ||
      !/^[a-f0-9]{64}$/.test(value.sha256) ||
      !Object.hasOwn(parsed.printProfiles, value.dataRef) ||
      typeof parsed.printProfiles[value.dataRef] !== "string"
    )
      throw new Error("PRINT_PROFILE_INVALID");
    // Explicit paths prevent interpreting lookalike objects in opaque legacy data.
    parent.printProfile = {
      name: value.name,
      sha256: value.sha256,
      data: parsed.printProfiles[value.dataRef],
    };
  }
  if (Array.isArray(pdfReferences) && pdfs) {
    for (const path of pdfReferences) {
      if (
        !Array.isArray(path) ||
        !path.length ||
        path.at(-1) !== "placedPdf" ||
        path.some((key: unknown) => typeof key !== "string")
      )
        return fail();
      let parent = parsed.state;
      for (const key of path.slice(0, -1)) {
        if (
          !parent ||
          typeof parent !== "object" ||
          !Object.hasOwn(parent, key)
        )
          return fail();
        parent = parent[key];
      }
      if (
        !parent ||
        typeof parent !== "object" ||
        !Object.hasOwn(parent, "placedPdf")
      )
        return fail();
      const value = parent.placedPdf;
      if (
        !value ||
        typeof value !== "object" ||
        Object.keys(value).sort().join(",") !==
          "assetId,dataRef,height,sha256,version,width" ||
        value.version !== 1 ||
        typeof value.assetId !== "string" ||
        typeof value.width !== "number" ||
        typeof value.height !== "number" ||
        typeof value.sha256 !== "string" ||
        !/^[a-f0-9]{64}$/.test(value.sha256) ||
        value.dataRef !== value.sha256 ||
        !Object.hasOwn(pdfs, value.dataRef) ||
        typeof pdfs[value.dataRef] !== "string"
      )
        return fail();
      const { dataRef, ...descriptor } = value;
      parent.placedPdf = { ...descriptor, data: pdfs[dataRef] };
    }
  }
  return parsed.state;
}
